// SingleCampaign.js
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEventByAny,
  selectCurrentEvent,
  selectEventLoading,
} from "@/store/redux/slices/event/event_slice";
import {
  fetchSubEventsByOrg,
  selectSubEvents,
  selectSubEventLoading,
  resetSubEvents,
} from "@/store/redux/slices/event/sub_event_slice";
import { useLoader } from "@/hooks/LoaderProvider";
import RallyImage from "@/assets/images/rally_image.jpg";
import "@/assets/styles/custom_tabs.css";
import Breadcrumb from "@/components/specific/Breadcrumb";

// Import all components
import { Tabs, Button } from "@/config/antd";
import Back from "@/components/specific/Back";
import CampaignHeader from "@/pages/event_management/single_campaign/components/Campaign_Header";
import CampaignImage from "@/pages/event_management/single_campaign/components/Campaign_Image";
import CampaignDescription from "@/pages/event_management/single_campaign/components/Campaign_Description";
import LocationMapCard from "@/pages/event_management/single_campaign/components/Location_Map_Card";
import AllocatedFundsCard from "@/pages/event_management/single_campaign/components/Allocated_Funds_Card";
import ScheduleTab from "@/pages/event_management/single_campaign/components/campaign_tabs/Schedule_Tab";
import CalendarTab from "@/pages/event_management/single_campaign/components/campaign_tabs/Calender_Tab";
import AttendeesSection from "@/pages/event_management/single_campaign/components/attendees_tabs/Attendees_Section";
import Success from "@/components/common/alerts/Success";

const SingleCampaign = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("1");
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  const currentEvent = useSelector(selectCurrentEvent);
  const isLoading = useSelector(selectEventLoading);
  const subEvents = useSelector(selectSubEvents);
  const subEventLoading = useSelector(selectSubEventLoading);

  useEffect(() => {
    if (isLoading || subEventLoading) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoading, subEventLoading, showLoader, hideLoader]);

  useEffect(() => {
    // Clear previous sub-events when component mounts or when event ID changes
    dispatch(resetSubEvents());

    if (id) {
      const fetchEvent = async () => {
        await dispatch(fetchEventByAny({ key: "_id", value: id })).unwrap();
      };
      fetchEvent();
    }
  }, [id, dispatch]);

  useEffect(() => {
    const fetchSubEvents = async () => {
      if (currentEvent?._id) {
        try {
          // Reset sub-events before fetching new ones
          await dispatch(resetSubEvents());

          await dispatch(
            fetchSubEventsByOrg({
              key: "eventID",
              value: currentEvent._id,
            })
          ).unwrap();
        } catch {
          console.error("No sub-events found for this event:");
        }
      }
    };

    fetchSubEvents();
  }, [currentEvent?._id, dispatch]);

  if (!currentEvent) {
    return <div>Event not found</div>;
  }

  // Extract state from location string
  const extractState = (locationStr) => {
    if (!locationStr) return "";
    const parts = locationStr.split(",").map((part) => part.trim());

    if (parts.length >= 3) {
      // For format like "Gwarinpa, Abuja, Nigeria" - return "Abuja, Nigeria"
      return parts.slice(1).join(", ");
    } else if (parts.length === 2) {
      // For format like "Abuja, Nigeria" - return "Nigeria"
      return parts[1];
    }
    return parts[0];
  };

  // Calculate total budget from all subevents
  const calculateTotalBudget = () => {
    if (!subEvents || subEvents.length === 0) return 0;

    return subEvents.reduce((total, subEvent) => {
      return total + (Number(subEvent?.budgetaryAllocation) || 0);
    }, 0);
  };

  // Transform API data and include sub-events
  const campaign = {
    _id: currentEvent?._id,
    id: currentEvent?.id,
    title: currentEvent?.name,
    fullDescription: currentEvent?.description,
    address: currentEvent?.location[0],
    scheduleCount: subEvents?.length || 0,
    image: currentEvent?.fileUrl || RallyImage,
    state: extractState(currentEvent?.location[0]),
    allocatedFunds: currentEvent?.allocatedFunds,
    spentPercentage: 28.5,
    totalBudget: calculateTotalBudget(),
    schedules:
      subEvents?.map((subEvent) => ({
        id: parseInt(subEvent.id, 10) || Math.floor(Math.random() * 1000000),
        _id: subEvent._id,
        title: subEvent?.name,
        address: subEvent?.location.address,
        location: subEvent?.location.venue,
        timeRange: `${subEvent?.duration?.startTime} - ${subEvent?.duration?.endTime}`,
        description: subEvent?.description,
        speakers: subEvent?.schedule.speakers,
        members: subEvent?.members,
        logistics: subEvent?.logistics,
      })) || [],
  };

  const handleDeleteSuccess = () => {
    setShowDeleteSuccess(true);
  };

  const handleSuccessClose = () => {
    setShowDeleteSuccess(false);
    navigate("/dashboard/events");
  };

  //  Navigation to include the parent event name
  const handleScheduleClick = () => {
    navigate(`/dashboard/events/${campaign._id}/add-sub-event/step-1`, {
      state: { parentEventName: currentEvent?.title },
    });
  };

  const moreOptionsMenu = {
    items: [
      { key: "edit", label: "Edit Campaign" },
      { key: "delete", label: "Delete Campaign" },
      { key: "share", label: "Share Campaign" },
    ],
  };

  const operations = (
    <Button
      type="link"
      className="px-0 font-medium text-primary"
      onClick={handleScheduleClick}>
      Add More
    </Button>
  );

  const items = [
    {
      label: "Schedules",
      key: "1",
      children: (
        <ScheduleTab
          eventName={currentEvent.name}
          schedules={campaign.schedules}
        />
      ),
    },
    {
      label: "Calendar",
      key: "2",
      children: <CalendarTab schedules={campaign.schedules} />,
    },
  ];

  const handleBackClick = () => {
    navigate("/dashboard/events");
  };

  // Define breadcrumb items with the actual event name
  const breadcrumbItems = [
    { title: "Events", link: "/dashboard/events" },
    { title: currentEvent?.name || "Event Details", link: null },
  ];

  return (
    <div data-testid="single-campaign-container" className="w-full">
      <div className="mb-3 mt-5">
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className="mt-5">
        <Back
          text="Back"
          bold={true}
          color="black"
          onClick={handleBackClick}
          className="text-bold"
        />
      </div>

      <CampaignHeader
        campaign={campaign}
        moreOptionsMenu={moreOptionsMenu}
        onDeleteSuccess={handleDeleteSuccess}
      />

      {/* Success Modal */}
      <Success
        isVisible={showDeleteSuccess}
        onClose={handleSuccessClose}
        header="Event Deleted"
        paragraph="The event has been successfully deleted."
        buttonText="Done"
        showCancelButton={false}
        onButtonClick={handleSuccessClose}
      />

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
        <div className="col-span-2">
          <CampaignImage image={campaign.image} title={campaign.title} />

          <CampaignDescription
            data-testid="campaign-description"
            description={campaign.fullDescription}
          />

          <div className="lg:hidden">
            <LocationMapCard
              address={campaign.address}
              state={campaign.state}
            />

            <AllocatedFundsCard
              totalBudget={campaign.totalBudget}
              spentPercentage={campaign?.spentPercentage}
            />
          </div>

          <div className="mt-8">
            <Tabs
              defaultActiveKey="1"
              activeKey={activeTab}
              onChange={setActiveTab}
              items={items}
              tabBarExtraContent={{ right: operations }}
              className={`custom-tabs ${
                activeTab === "1" ? "schedule-tab" : "calendar-tab"
              }`}
              tabBarStyle={{
                marginBottom: "24px",
              }}
            />
          </div>

          <AttendeesSection />
        </div>
        <div className="hidden lg:block">
          <LocationMapCard address={campaign.address} state={campaign.state} />

          <AllocatedFundsCard
            totalBudget={campaign.totalBudget}
            spentPercentage={campaign?.spentPercentage}
          />
        </div>
      </div>
    </div>
  );
};

export default SingleCampaign;
